/**
 * TypingMessage Component
 *
 * Displays text with a typewriter effect — one GRAPHEME at a time (a UTF-16
 * slice split emoji and combining accents mid-character), never for a reader
 * who asked for reduced motion (the whole text at once), and read once, whole,
 * by a screen reader: the growing prefix is hidden from it (JW-63).
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import { cn } from '../lib/utils';
import { usePrefersReducedMotion } from '../lib/use-prefers-reduced-motion';

const graphemes = (text: string): string[] =>
  Array.from(
    new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(text),
    (s) => s.segment
  );

export interface TypingMessageProps {
  /** Text content to display with typing effect */
  content: string;
  /** Characters per second (default: 30) */
  speed?: number;
  /** Callback when typing animation completes */
  onComplete?: () => void;
  /** Additional CSS classes */
  className?: string;
}

export function TypingMessage({ content, speed = 30, onComplete, className }: TypingMessageProps) {
  const reduceMotion = usePrefersReducedMotion();
  const chars = useMemo(() => graphemes(content), [content]);
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!content) return;

    if (reduceMotion) {
      setDisplayedText(content);
      setIsTyping(false);
      onComplete?.();
      return;
    }

    setDisplayedText('');
    setIsTyping(true);
    let currentIndex = 0;

    const typeNextChar = () => {
      if (currentIndex < chars.length) {
        currentIndex++;
        setDisplayedText(chars.slice(0, currentIndex).join(''));
        const delay = 1000 / speed;
        timeoutRef.current = setTimeout(typeNextChar, delay);
      } else {
        setIsTyping(false);
        if (onComplete) {
          onComplete();
        }
      }
    };

    timeoutRef.current = setTimeout(typeNextChar, 50);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [content, chars, speed, onComplete, reduceMotion]);

  return (
    // `whitespace-pre-line` here as well as on the settled bubble: the typing
    // animation renders a growing PREFIX of the same text, so without it the
    // paragraph break is absent while typing and appears when typing stops —
    // the text visibly reflows at the end of every message. (F34)
    // `data-typing` says whether the message is still being typed — while it
    // is, the screen-reader copy already holds the whole text, so "the text
    // is there" is not "the message has settled".
    <span className={cn('whitespace-pre-line', className)} data-typing={isTyping}>
      {isTyping ? (
        <>
          {/* A screen reader gets the message once, whole — never the growing
              prefix; the finished message is one plain text node again. */}
          <span className="sr-only">{content}</span>
          <span aria-hidden="true">
            {displayedText}
            <span className="inline-block w-0.5 h-4 bg-current ml-0.5 animate-pulse" />
          </span>
        </>
      ) : (
        displayedText
      )}
    </span>
  );
}
